import React, { useEffect, useState } from 'react'
import { Spinner } from '../Spinner'
import { TooltipPopOver } from '../TooltipPopOver'

export type ImageProps = {
  url: string
  /** The hover text, and the `img` alt text unless `alt` is given. */
  name?: string
  alt?: string
  className?: string
  fallbackUrl?: string
  style?: React.CSSProperties
  showLoading?: boolean
} & Pick<React.ImgHTMLAttributes<HTMLImageElement>, 'loading' | 'width' | 'height'>
export const Image = ({
  url,
  name,
  alt,
  className = 'img-fluid',
  fallbackUrl,
  style,
  showLoading = false,
  loading,
  width,
  height,
}: ImageProps) => {
  const [imageSrc, setImageSrc] = useState(url)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setImageSrc(url)
    setIsLoading(true)
  }, [url])

  const onErrorImage = () => {
    const fallback =
      fallbackUrl ?? 'https://res.cloudinary.com/tmmshauler/image/upload/v1626700322/umbrella/image-not-available.jpg'
    setImageSrc(fallback)
    setIsLoading(false)
  }

  const onLoadImage = () => {
    setIsLoading(false)
  }

  const imageNode = (
    <TooltipPopOver tooltipBody={name}>
      <img
        className={`${className}`}
        src={imageSrc}
        alt={alt ?? name}
        loading={loading}
        width={width}
        height={height}
        onError={onErrorImage}
        onLoad={onLoadImage}
        style={{
          ...style,
          opacity: isLoading && showLoading ? 0.5 : 1,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />
    </TooltipPopOver>
  )

  //show loading is false, no need for extra div
  if (!showLoading) {
    return imageNode
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {isLoading && showLoading && (
        <Spinner
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            zIndex: 1,
          }}
        ></Spinner>
      )}
      {imageNode}
    </div>
  )
}

export type ImagesProps = {
  dataArray?: ImageProps[]
  className?: string
  renderImage?: (m: ImageProps, i: number) => React.ReactNode
}
export const Images = ({ dataArray, className, renderImage }: ImagesProps) => {
  if (!dataArray || dataArray.length <= 0) return null

  //setup the default imageItem rendering if not specified
  if (!renderImage) {
    renderImage = (m, i) => {
      return <Image key={i} url={m.url} name={m.name} className={className} />
    }
  }

  return <div>{dataArray.map(renderImage)}</div>
}
